import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { AccordionModule } from 'primeng/accordion';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { afterRender } from '../../../common/after-render';
import { notifyAndTrackError } from '../../../common/error';
import { getScientificName, isEmpty } from '../../../common/functions';
import { insectGrowthStageSort } from '../../../common/sorts';
import { successMsg, warningMsg } from '../../../common/messages';
import { aggregateValidation } from '../../../common/validation';

import { CanComponentDeactivate, ProcessingComponent, TaxonSearchComponent, UnsavedChangesDialogComponent } from '../../../components';

import { AppInsightsService } from '../../../services/app-insights.service';
import {
  GrowthStageAdminService,
  IGrowthStage,
  IInsect,
  IInsectAddRequest,
  IInsectGrowthStage,
  IInsectGrowthStageAddRequest,
  IInsectSynonym,
  IInsectSynonymAddRequest,
  IInsectVariant,
  InsectAdminService,
  ITaxon,
  IUploadUrlResult,
  IVariantType,
  UploadService,
  VariantTypeAdminService
} from '../../../services/insecta';
import { MSGraphService } from '../../../services/msgraph.service';

@Component({
  selector: 'app-insect-admin',
  standalone: true,
  imports: [
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    ChipModule,
    CommonModule,
    AccordionModule,
    DialogModule,
    FormsModule,
    FloatLabelModule,
    FileUploadModule,
    InputTextModule,
    InputNumberModule,
    RouterModule,
    TableModule,
    ToggleSwitchModule,
    SelectModule,
    ProcessingComponent,
    TaxonSearchComponent,
    UnsavedChangesDialogComponent,
    ImageModule,
    TooltipModule
  ],

  templateUrl: 'insect-admin.component.html'
})
export class InsectAdminComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  insects: IInsect[] = [];
  modifiedinsects: { [s: string]: IInsect } = {};
  taxon: ITaxon | null = null;
  displayOtherNameDialog: boolean = false;
  newSynonym: string | null = null;
  displayVariantDialog: boolean = false;
  displayGrowthStageDialog: boolean = false;
  variantTypes: IVariantType[] = [];
  variantTypesCopy: IVariantType[] = [];
  growthStages: IGrowthStage[] = [];
  growthStagesCopy: IGrowthStage[] = [];
  newInsectVariant: IInsectVariant = <IInsectVariant>{};
  newGrowthStage: IInsectGrowthStage = <IInsectGrowthStage>{};
  dialogConfirmSub: ((leave: boolean) => void) | null = null;
  @ViewChild('fileUploader') fileUploader: any;
  isFetchingImage = false;

  dialogVars: any = {
    visible: false,
    data: <IInsect>{}
  };

  pageVars: any = {
    currentUser: '',
    isEditing: false,
    isReady: false,
    showUnsavedChangesDialog: false,
    pageId: 'Admin:Insects',
    activeIndex: null,
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Insect Management' }]
  };

  expandedRows = {};

  constructor(
    private apiService: InsectAdminService,
    private appInsights: AppInsightsService,
    private growthService: GrowthStageAdminService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private variantService: VariantTypeAdminService,
    private uploadService: UploadService,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
    @Inject(SETTINGS) settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:Insects`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/insects`);
    this.dialogVars.data = <IInsect>{};
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.pageVars.showUnsavedChangesDialog = true;

      const confirmSub = (decision: boolean) => {
        this.dialogConfirmSub = null;
        resolve(decision);
      };

      this.dialogConfirmSub = confirmSub;
    });
  }

  onDialogConfirm(leave: boolean) {
    this.pageVars.showUnsavedChangesDialog = false;
    if (this.dialogConfirmSub) {
      this.dialogConfirmSub(leave);
    }
  }

  ngOnDestroy() {
    this.insects = [];
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);

    const request = {
      profile: this.graphService.getMyProfile$(),
      insects: this.apiService.getInsects(true),
      variantTypes: this.variantService.getVariantTypesAll(),
      growthStages: this.growthService.getGrowthStagesAll()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.insects = response.insects;
        this.insects.sort((a, b) => a.scientificName.localeCompare(b.scientificName));
        this.pageVars.currentUser = response.profile.userPrincipalName;
        this.variantTypes = response.variantTypes;
        this.variantTypesCopy = response.variantTypes;
        this.growthStages = response.growthStages;
        this.growthStagesCopy = response.growthStages.filter((x) => x.isActive);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: 'Init:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  backClick() {
    this.router.navigate(['admin/globallists']);
  }

  onTaxonChange(taxon: any) {
    if (taxon != null) {
      const scientificName = getScientificName(<ITaxon>taxon);
      this.dialogVars.data.taxonJson = JSON.stringify(taxon);
      this.dialogVars.data.scientificName = scientificName;
    } else {
      this.dialogVars.data.taxonJson = null;
      this.dialogVars.data.scientificName = null;
    }
  }

  addOtherNameShow() {
    this.displayOtherNameDialog = true;
  }

  removeOtherNames(synonym: IInsectSynonymAddRequest) {
    this.dialogVars.data.insectSynonyms = this.dialogVars.data.insectSynonyms.filter((s: IInsectSynonymAddRequest) => s.name !== synonym.name);
  }

  getOtherNames(insect: IInsect): string {
    if (insect.insectSynonyms && insect.insectSynonyms.length > 0) {
      return insect.insectSynonyms.map((x) => x.name).join(', ');
    }
    return '';
  }

  otherNamesDialogIsValid(): boolean {
    if (isEmpty(this.newSynonym)) return false;
    if (this.newSynonym == null) return false;
    return true;
  }

  createOtherNamesDlgSave() {
    if (this.newSynonym == null || isEmpty(this.newSynonym)) {
      warningMsg(this.msgService, 'Please enter a synonym');
      return;
    }

    if (this.dialogVars.data.insectSynonyms.some((synonym: IInsectSynonymAddRequest) => synonym.name === this.newSynonym)) {
      warningMsg(this.msgService, 'Synonym already exists');
      return;
    }

    const synonym: IInsectSynonymAddRequest = {
      name: this.newSynonym!,
      isActive: true,
      actionBy: this.pageVars.currentUser,
      action: '',
      actionOn: new Date(Date.now())
    };

    this.dialogVars.data.insectSynonyms.push(synonym);
    this.newSynonym = null;
    this.displayOtherNameDialog = false;
  }

  createOtherNamesDlgCancel() {
    this.newSynonym = null;
    this.displayOtherNameDialog = false;
  }

  openVariantDialog() {
    this.displayVariantDialog = true;
    this.newInsectVariant.isActive = true;
    this.newInsectVariant.actionBy = this.pageVars.currentUser;
    this.newInsectVariant.action = '';
    this.newInsectVariant.actionOn = new Date(Date.now());

    const selectedVariants = this.dialogVars.data.insectVariants.map((x: any) => x.variantTypeId);
    if (selectedVariants && selectedVariants.length > 0) {
      this.variantTypesCopy = this.variantTypes.filter((vt) => !selectedVariants.includes(vt.id));
    } else {
      this.variantTypesCopy = this.variantTypes;
    }
    const defaultVariantType = this.variantTypesCopy.length > 0 ? this.variantTypesCopy.find((vt) => vt.isDefault) : undefined;
    this.newInsectVariant.variantTypeId = defaultVariantType !== undefined ? defaultVariantType.id : '';
    this.newInsectVariant.description = '';
  }

  getVariantTypeName(variantTypeId: string): string {
    return this.variantTypes.filter((x) => x.id === variantTypeId)[0]?.name;
  }

  createVariantDlgCancel() {
    this.displayVariantDialog = false;
    this.newInsectVariant = <IInsectVariant>{};
  }

  createVariantDlgSave() {
    if (this.dialogVars.data.insectVariants.some((variant: IInsectVariant) => variant.variantTypeId === this.newInsectVariant.variantTypeId)) {
      warningMsg(this.msgService, 'Variant already exists');
      return;
    }

    this.dialogVars.data.insectVariants.push(this.newInsectVariant);
    this.createVariantDlgCancel();
  }

  getInsectVariantTypeName(variantTypeId: string): string {
    const variantType = this.variantTypes.find((vt) => vt.id === variantTypeId);
    return variantType ? variantType.name : 'Unknown';
  }

  openGrowthStageDialog() {
    this.newGrowthStage = <IInsectGrowthStage>{};
    this.displayGrowthStageDialog = true;

    const selectedGrowthStage = this.dialogVars.data.insectGrowthStages.map((x: any) => x.growthStageId);
    if (selectedGrowthStage && selectedGrowthStage.length > 0) {
      this.growthStagesCopy = this.growthStages.filter((vt) => !selectedGrowthStage.includes(vt.id) && vt.isActive);
    } else {
      this.growthStagesCopy = this.growthStages.filter((vt) => vt.isActive);
    }
    const defaultGrowthStage = this.growthStagesCopy.length > 0 ? this.growthStagesCopy.find((gs) => gs.isDefault) : undefined;
    this.newGrowthStage.growthStageId = defaultGrowthStage !== undefined ? defaultGrowthStage.id : '';
    this.newGrowthStage.degreeDays = 0;
    this.newGrowthStage.actionBy = this.pageVars.currentUser;
    this.newGrowthStage.uploadId = undefined;
    this.newGrowthStage.isActive = true;
  }

  getGrowthStagename(growthStageId: string): string {
    return this.growthStages.filter((x) => x.id === growthStageId)[0]?.name;
  }

  isGrowthStageTypeActive(growthStageId: string): boolean {
    const growthStage = this.growthStages.find((x) => x.id === growthStageId);
    return growthStage?.isActive ?? false;
  }

  createGrowthStageDlgCancel() {
    this.displayGrowthStageDialog = false;
    this.newGrowthStage = <IInsectGrowthStage>{};
    this.fileUploader.clear();
  }

  async onFileUpload(event: any, index = 9999) {
    const file: File = event.files[0];
    this.pageVars.isReady = false;

    firstValueFrom(this.uploadService.uploadImage(file))
      .then((uploadId: string) => {
        if (index !== 9999) {
          this.dialogVars.data.insectGrowthStages[index].uploadId = uploadId;
          (async () => {
            this.isFetchingImage = true;
            this.dialogVars.data.insectGrowthStages[index].imageUrl = undefined;
            const imageUrl = await this.linkImage(uploadId);
            this.dialogVars.data.insectGrowthStages[index].imageUrl = imageUrl;
            this.changeDetector.detectChanges();
            this.isFetchingImage = false;
          })();
        } else {
          this.newGrowthStage.uploadId = uploadId;
          (async () => {
            this.isFetchingImage = true;
            this.newGrowthStage.imageUrl = undefined;
            const imageUrl = await this.linkImage(uploadId);
            this.newGrowthStage.imageUrl = imageUrl;
            this.changeDetector.detectChanges();
            this.isFetchingImage = false;
          })();
        }
        successMsg(this.msgService, 'File uploaded successfully');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: 'FileUpload:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  onFileUploadError(event: any) {
    console.log(event);
  }

  getImage(growthStage: IInsectGrowthStageAddRequest): string | null {
    if (growthStage.uploadId) {
      return `${this.uploadService.getUploadUrl(growthStage.uploadId)}`;
    }
    return null;
  }

  createGrowthStageDlgSave() {
    if (
      this.dialogVars.data.insectGrowthStages.some((stage: IInsectGrowthStageAddRequest) => stage.growthStageId === this.newGrowthStage.growthStageId)
    ) {
      warningMsg(this.msgService, 'Growth Stage already exists');
      return;
    }
    this.dialogVars.data.insectGrowthStages.push(this.newGrowthStage);
    this.createGrowthStageDlgCancel();
  }

  growthStageDlgValid(): boolean {
    return true;
  }

  variantDlgValid(): boolean {
    return true;
  }

  gridNewRow() {
    this.taxon = null;
    this.dialogVars.data = <IInsect>{
      scientificName: '',
      taxonJson: '',
      isActive: true,
      actionBy: this.pageVars.currentUser,
      insectSynonyms: <IInsectSynonym[]>[],
      insectVariants: <IInsectVariant[]>[],
      insectGrowthStages: <IInsectGrowthStage[]>[]
    };
    this.dialogVars.visible = true;
    this.dialogVars.data.action = 'INS';
  }

  editRow(Insect: IInsect) {
    this.taxon = JSON.parse(Insect.taxonJson) as ITaxon;
    this.dialogVars.data = JSON.parse(JSON.stringify(Insect));
    this.dialogVars.data.insectGrowthStages = [
      ...this.dialogVars.data.insectGrowthStages.sort((a: IInsectGrowthStage, b: IInsectGrowthStage) => insectGrowthStageSort(a, b))
    ];

    if (this.dialogVars.data.insectVariants) {
      this.dialogVars.data.insectVariants.forEach((variant: any) => {
        delete variant.insectVariantCodes;
      });
    }

    this.dialogVars.data.insectGrowthStages.map((stage: any) => {
      stage.imageUrl = undefined;
    });

    this.dialogVars.visible = true;
    this.dialogVars.data.action = 'UPD';

    this.dialogVars.data.insectGrowthStages.map((stage: any) => {
      if (stage.uploadId) {
        afterRender(async () => {
          const imageUrl = await this.linkImage(stage.uploadId);
          stage.imageUrl = imageUrl;
          this.changeDetector.detectChanges();
        });
      } else {
        stage.imageUrl = '';
      }
    });
    afterRender(() => {
      this.pageVars.activeIndex = -1;
      this.changeDetector.detectChanges();
    });
  }

  dialogIsValid(): boolean {
    if (
      isEmpty(this.dialogVars.data.scientificName) ||
      this.dialogVars.data.scientificName.trim() === '' ||
      isEmpty(this.dialogVars.data.taxonJson) ||
      this.dialogVars.data.insectVariants.length === 0 ||
      this.dialogVars.data.insectGrowthStages.length === 0
    ) {
      return false;
    }
    if (this.dialogVars.data.insectVariants !== undefined && this.dialogVars.data.insectVariants.length > 0) {
      for (const variant of this.dialogVars.data.insectVariants) {
        if (isEmpty(variant.variantTypeId) || isEmpty(variant.description)) {
          return false;
        }
      }
    }
    return true;
  }

  createDlgSave() {
    if (this.insects.some((i: IInsect) => i.scientificName === this.dialogVars.data.scientificName) && this.dialogVars.data.action === 'INS') {
      warningMsg(this.msgService, 'Insect with the same scientific name already exists.');
      return;
    }
    this.dialogVars.data.actionBy = this.pageVars.currentUser;
    this.dialogVars.data.actionOn = new Date(Date.now());
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Insert`, { Insect: this.dialogVars.data });

    const action =
      this.dialogVars.data.action === 'INS' ? this.apiService.addInsect(this.dialogVars.data) : this.apiService.updateInsect(this.dialogVars.data);

    this.pageVars.isReady = false;
    firstValueFrom(action)
      .then((result: any) => {
        this.dialogVars.data.id = result;
        this.insects = [];

        firstValueFrom(this.apiService.getInsects(true))
          .then((insects: IInsect[]) => {
            this.insects = insects;
            this.changeDetector.detectChanges();
            successMsg(this.msgService, 'Insect has been saved');

            this.dialogVars.visible = false;
            this.dialogVars.data = <IInsect>{};

            this.pageVars.isEditing = false;
            this.pageVars.activeIndex = -1;
          })
          .catch((err) => {
            notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: 'Grid:RefreshAfterSave:Error' });
          });
      })
      .catch((err) => {
        if ((<string>err.message).includes(`Validation`) && err.failures?.length) {
          const rules = err.failures.map((f: any) => ({ valid: false, message: `${f.field}: ${f.message}` }));
          aggregateValidation(this.msgService, rules, { dedupe: true });
        } else {
          const actionName = this.dialogVars.data.action === 'INS' ? 'Grid:Insert:Error' : 'Grid:Update:Error';
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: actionName });
        }
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  createDlgCancel() {
    this.dialogVars.visible = false;
    this.dialogVars.data = <IInsectAddRequest>{};
  }

  gridRowEditInit(c: IInsect) {
    this.pageVars.isEditing = true;
    this.modifiedinsects[c.id as string] = { ...c };
    c.action = 'UPD';
    c.actionBy = this.pageVars.currentUser;
    c.actionOn = new Date(Date.now());
  }

  public linkImage(fileName: string | null): Promise<string> {
    return new Promise((resolve, rejects) => {
      this.isFetchingImage = true;
      let sasPath = ``;
      if (isEmpty(fileName)) {
        resolve(sasPath);
      }

      firstValueFrom(this.uploadService.getUploadUrl(<string>fileName))
        .then((result: IUploadUrlResult) => {
          sasPath = result.url;
          resolve(sasPath);
          this.isFetchingImage = false;
        })
        .catch((err) => {
          rejects(err);
          this.isFetchingImage = false;
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: 'LinkImage:Error' });
          notifyAndTrackError(this.msgService, this.appInsights, 'Failed to get image URL', {
            component: 'InsectAdminComponent',
            action: 'LinkImage:Message'
          });
        });
    });
  }

  onInsectStatusChange(insect: IInsect | any) {
    insect.actionBy = this.pageVars.currentUser;
    insect.actionOn = new Date(Date.now());
    insect.action = insect.isActive ? 'ACT' : 'INA';
    this.pageVars.isReady = false;

    firstValueFrom(this.apiService.updateInsect(insect))
      .then(() => {
        successMsg(this.msgService, 'Insect status updated successfully');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'InsectAdminComponent', action: 'ToggleStatus:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.activeIndex = -1;
      });
  }

  deleteImage(growthStage: IInsectGrowthStageAddRequest, index: number) {
    if (growthStage.uploadId) {
      this.dialogVars.data.insectGrowthStages[index].uploadId = null;
      this.dialogVars.data.insectGrowthStages[index].imageUrl = '';
      successMsg(this.msgService, 'Image deleted, Please save the insect for changes to take effect');
    }
  }
}
